import { Link, useParams } from "react-router-dom";
import ProductCard from "@/components/feature/ProductCard";
import Reveal from "@/components/feature/Reveal";
import { products } from "@/mocks/products";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const product = products.find((item) => item.id === id);

  if (!product) {
    return (
      <section className="flex min-h-[70vh] items-center justify-center bg-background-50 px-4 pt-24">
        <div className="text-center">
          <h1 className="font-heading text-3xl text-foreground-950">제품을 찾을 수 없습니다</h1>
          <p className="mt-3 text-sm text-foreground-600">
            요청하신 소재가 삭제되었거나 주소가 잘못되었을 수 있습니다.
          </p>
          <Link
            to="/products"
            className="mt-7 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" /> 제품 목록으로
          </Link>
        </div>
      </section>
    );
  }

  const related = products.filter((item) => item.category === product.category && item.id !== product.id).slice(0, 4);
  const fallbackRelated =
    related.length > 0 ? related : products.filter((item) => item.id !== product.id).slice(0, 4);

  const specs = [
    { label: "카테고리", value: product.category, icon: "ri-price-tag-3-line" },
    { label: "재질", value: product.material, icon: "ri-stack-line" },
    { label: "원산지", value: product.origin, icon: "ri-earth-line" },
    { label: "마감", value: product.finish, icon: "ri-brush-line" },
    { label: "두께", value: product.thickness, icon: "ri-ruler-2-line" },
    { label: "규격", value: product.size, icon: "ri-aspect-ratio-line" },
    { label: "컬러", value: product.color, icon: "ri-palette-line" },
  ];

  return (
    <>
      <section className="bg-background-50 pt-28 md:pt-32">
        <div className="mx-auto max-w-content px-4 md:px-6">
          <nav aria-label="현재 위치" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
              홈
            </Link>
            <span>/</span>
            <Link to="/products" className="cursor-pointer transition-colors hover:text-primary-600">
              제품소개
            </Link>
            <span>/</span>
            <span className="text-foreground-800">{product.name}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-8 pb-12 lg:grid-cols-2 lg:gap-12">
            <div className="h-[340px] w-full overflow-hidden rounded-lg bg-secondary-100 md:h-[520px]">
              <img
                src={product.image}
                alt={`${product.name} ${product.category}`}
                title={`${product.name} 동우스톤 ${product.category}`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="flex flex-col justify-center">
              <span className="inline-flex w-fit rounded-full bg-primary-100 px-3.5 py-1.5 font-label text-xs font-medium text-primary-700">
                {product.category}
              </span>
              <p className="mt-5 font-label text-xs tracking-[0.24em] text-foreground-500">{product.nameEn}</p>
              <h1 className="mt-2 font-heading text-3xl text-foreground-950 md:text-4xl">{product.name}</h1>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">{product.summary}</p>

              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/contact"
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
                >
                  <i className="ri-customer-service-2-line" /> 견적 상담하기
                </Link>
                <Link
                  to="/projects"
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-300 px-6 py-3.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 sm:w-auto"
                >
                  <i className="ri-image-2-line" /> 시공 사례 보기
                </Link>
              </div>

              <dl className="mt-9 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-background-200 pt-7">
                {specs.map((spec) => (
                  <div key={spec.label} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-primary-600">
                      <i className={`${spec.icon} text-base`} />
                    </span>
                    <div>
                      <dt className="text-xs text-foreground-500">{spec.label}</dt>
                      <dd className="mt-0.5 text-sm font-medium text-foreground-900">{spec.value}</dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-14">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl text-foreground-950">제품 설명</h2>
              <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">{product.description}</p>
            </div>
            <div>
              <h2 className="font-heading text-2xl text-foreground-950">적용 공간</h2>
              <ul className="mt-5 space-y-3">
                {product.applications.map((application) => (
                  <li key={application} className="flex items-center gap-3 text-sm text-foreground-700">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 text-accent-800">
                      <i className="ri-check-line text-sm" />
                    </span>
                    {application}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">함께 살펴보면 좋은 소재</h2>
            <Link
              to="/products"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              전체 제품 보기 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div data-product-shop className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {fallbackRelated.map((item, index) => (
              <Reveal key={item.id} delay={index * 70}>
                <ProductCard product={item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}